import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { usePermissionLevel } from "../../../hooks/usePermissionLevel";
import {
  getSessionDetail,
  type AdminSessionDetail,
  type AdminSessionStatus,
} from "../../../services/adminSessionsService";
import PersonaAvatar from "../../PersonaAvatar";
import MarkdownMessage from "../../MarkdownMessage";
import ReportCardPage from "../../report-card/ReportCardPage";
import { sessionsSearch } from "./adminSessionsUrl";
import {
  STATUS_LABEL,
  STATUS_TONE,
  defaultPersonaId,
  formatDateTime,
  personaEntries,
} from "./sessionsModel";

export interface AdminSessionPageProps {
  getToken: () => string | undefined;
}

type TabId = "personas" | "coach" | "submission" | "evaluation";

const TABS: { id: TabId; label: string }[] = [
  { id: "personas", label: "Personas" },
  { id: "coach", label: "Coach" },
  { id: "submission", label: "Submission" },
  { id: "evaluation", label: "Evaluation" },
];

function statusOf(detail: AdminSessionDetail): AdminSessionStatus {
  if (detail.reportCard) return "graded";
  if (detail.submission) return "submitted";
  return "not_submitted";
}

const label = "font-label text-xs uppercase tracking-widest text-slate-500";

/**
 * Read-only view of one learner's session for platform admins. Deliberately
 * has no inputs that write: nothing here can send a message, ask the coach or
 * grade.
 */
const AdminSessionPage: React.FC<AdminSessionPageProps> = ({ getToken }) => {
  const { isAdmin } = usePermissionLevel();
  const navigate = useNavigate();
  const location = useLocation();
  const { orgSlug, sessionId } = useParams<{ orgSlug: string; sessionId: string }>();

  // The prop is a new function on every parent render, so depending on it
  // would refetch on every navigation.
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  const [detail, setDetail] = useState<AdminSessionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabId>("personas");
  const [personaId, setPersonaId] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin || !sessionId) return;
    const controller = new AbortController();
    setDetail(null);
    setError(null);
    getSessionDetail(sessionId, getTokenRef.current(), controller.signal)
      .then(setDetail)
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : "Could not load this session.");
      });
    return () => controller.abort();
  }, [isAdmin, sessionId]);

  const personas = useMemo(
    () => (detail ? personaEntries(detail.personas, detail.messages) : []),
    [detail],
  );
  useEffect(() => setPersonaId(defaultPersonaId(personas)), [personas]);

  if (!isAdmin) {
    return (
      <div className="flex flex-col flex-1 h-full min-w-0 overflow-y-auto bg-background">
        <div className="max-w-5xl w-full mx-auto px-8 py-10 flex flex-col items-center justify-center text-center min-h-full">
          <span className="material-symbols-outlined text-5xl text-slate-500 mb-4">lock</span>
          <h2 className="font-headline text-3xl font-bold tracking-tight text-on-surface mb-2">
            Admin access required
          </h2>
          <p className="text-slate-500 font-body text-sm max-w-md">
            You need to be an admin to access this page. Contact an administrator
            in your organization if you believe this is a mistake.
          </p>
        </div>
      </div>
    );
  }

  const from = (location.state as { from?: string } | null)?.from;
  const goBack = () => {
    const search =
      from ?? sessionsSearch({ user: detail?.user.userId ?? null, page: 1, q: "" });
    navigate(`/${orgSlug}/admin?${search}`);
  };

  const backButton = (
    <button
      type="button"
      onClick={goBack}
      className="mb-6 flex items-center gap-1 text-xs font-label uppercase tracking-wider text-slate-400 transition-colors hover:text-on-surface"
    >
      <span className="material-symbols-outlined text-base">arrow_back</span>
      Back to sessions
    </button>
  );

  if (!detail) {
    return (
      <div className="flex flex-col flex-1 h-full min-w-0 overflow-y-auto bg-background">
        <div className="max-w-6xl w-full mx-auto px-8 py-10">
          {backButton}
          <p className={error ? "text-sm text-error font-body" : label}>
            {error ?? "Loading session…"}
          </p>
        </div>
      </div>
    );
  }

  const status = statusOf(detail);
  const documentName = new Map(detail.documents.map((d) => [d.id, d.name]));
  const selectedPersona = personas.find((p) => p.id === personaId) ?? null;
  const transcript = detail.messages.filter((m) => m.personaId === personaId);

  return (
    <div className="flex flex-col flex-1 h-full min-w-0 bg-background">
      <div className="max-w-6xl w-full mx-auto px-8 pt-10">
        {backButton}
        <header className="mb-6">
          <p className={label}>
            {detail.user.email ?? detail.user.stytchMemberId}
            {detail.user.name ? ` · ${detail.user.name}` : ""}
            {` · ${detail.user.organizationName ?? detail.user.organizationId}`}
          </p>
          <h2 className="font-headline text-3xl font-bold tracking-tight text-on-surface mt-1">
            {detail.scenario?.title ?? "Unknown scenario"} · Session {detail.session.sessionNumber}
            {detail.session.name ? ` — ${detail.session.name}` : ""}
          </h2>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2 text-sm font-body text-slate-500">
            <span>Started {formatDateTime(detail.session.createdAt)}</span>
            <span>Last active {formatDateTime(detail.session.lastActiveAt)}</span>
            <span>{detail.messages.length} messages</span>
            <span>{detail.coachTurns.length} coach turns</span>
            <span
              className={`text-xs font-label font-bold tracking-widest uppercase px-2 py-1 rounded border ${STATUS_TONE[status]}`}
            >
              {STATUS_LABEL[status]}
            </span>
          </div>
        </header>

        <div className="flex gap-6 border-b border-outline-variant/30">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`pb-3 -mb-px font-label text-xs uppercase tracking-widest transition-colors border-b-2 ${
                tab === t.id
                  ? "border-primary text-on-surface"
                  : "border-transparent text-slate-500 hover:text-on-surface"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "evaluation" && detail.reportCard ? (
        // ReportCardPage owns its own scroll container.
        <ReportCardPage getToken={getToken} card={detail.reportCard} hideBackToChat />
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="max-w-6xl w-full mx-auto px-8 py-6">
            {tab === "personas" && (
              <div className="grid grid-cols-1 md:grid-cols-[16rem_minmax(0,1fr)] gap-4">
                <div className="flex flex-col border border-outline-variant/30 rounded bg-surface-container">
                  {personas.length === 0 && (
                    <p className="p-4 text-sm text-slate-500 font-body">No personas.</p>
                  )}
                  {personas.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPersonaId(p.id)}
                      className={`flex items-center gap-3 px-3 py-2 text-left border-b border-outline-variant/10 transition-colors ${
                        p.id === personaId ? "bg-primary/10" : "hover:bg-surface-container-high"
                      }`}
                    >
                      <PersonaAvatar name={p.name} src={p.avatarUrl} className="w-8 h-8 rounded-full shrink-0" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-body text-on-surface">{p.name}</span>
                        <span className="block truncate text-xs font-body text-slate-500">{p.role}</span>
                      </span>
                      <span className="text-xs font-label text-slate-500">{p.messageCount}</span>
                    </button>
                  ))}
                </div>
                <div className="flex flex-col gap-4 min-w-0">
                  {transcript.length === 0 && (
                    <p className="text-sm text-slate-500 font-body">
                      {selectedPersona
                        ? `The learner did not talk to ${selectedPersona.name}.`
                        : "Select a persona."}
                    </p>
                  )}
                  {transcript.map((m) => (
                    <div
                      key={m.id}
                      className={`rounded border px-4 py-3 ${
                        m.role === "user"
                          ? "border-primary/30 bg-primary/5"
                          : "border-outline-variant/30 bg-surface-container"
                      }`}
                    >
                      <p className={`${label} mb-1`}>
                        {m.role === "user" ? "Learner" : selectedPersona?.name ?? "Persona"} ·{" "}
                        {formatDateTime(new Date(m.timestamp).toISOString())}
                      </p>
                      <MarkdownMessage text={m.text} />
                      {m.revealedDocId && (
                        <p className="mt-2 text-xs font-body text-secondary">
                          Revealed: {documentName.get(m.revealedDocId) ?? m.revealedDocId}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === "coach" && (
              <div className="flex flex-col gap-4">
                {detail.coachTurns.length === 0 && (
                  <p className="text-sm text-slate-500 font-body">The learner did not use the coach.</p>
                )}
                {detail.coachTurns.map((turn, i) => (
                  <div key={`${turn.createdAt}-${i}`} className="rounded border border-outline-variant/30 bg-surface-container">
                    <p className={`${label} px-4 pt-3`}>
                      {turn.assistanceType} · {formatDateTime(turn.createdAt)}
                    </p>
                    {turn.userPrompt && (
                      <div className="px-4 pt-2">
                        <p className={`${label} mb-1`}>Learner</p>
                        <MarkdownMessage text={turn.userPrompt} />
                      </div>
                    )}
                    <div className="px-4 py-3">
                      <p className={`${label} mb-1`}>Coach</p>
                      <MarkdownMessage text={turn.response} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === "submission" && (
              <div className="flex flex-col gap-4">
                {!detail.submission && (
                  <p className="text-sm text-slate-500 font-body">Nothing submitted.</p>
                )}
                {detail.submission?.answer && (
                  <div className="rounded border border-outline-variant/30 bg-surface-container px-4 py-3">
                    <p className={`${label} mb-2`}>Written answer</p>
                    <MarkdownMessage text={detail.submission.answer} />
                  </div>
                )}
                {detail.submission?.artifactName && (
                  <div className="rounded border border-outline-variant/30 bg-surface-container px-4 py-3">
                    <p className={`${label} mb-2`}>Uploaded document</p>
                    {detail.submission.artifactUrl ? (
                      <a
                        href={detail.submission.artifactUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-sm font-body text-primary hover:underline"
                      >
                        <span className="material-symbols-outlined text-base">description</span>
                        {detail.submission.artifactName}
                      </a>
                    ) : (
                      <p className="text-sm font-body text-on-surface">
                        {detail.submission.artifactName}{" "}
                        <span className="text-slate-500">(download link unavailable)</span>
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {tab === "evaluation" && !detail.reportCard && (
              <p className="text-sm text-slate-500 font-body">Not graded.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSessionPage;
